import cx from 'classnames'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Unit, UnitLabel } from '@/shared/config/config'
import {
  InputGroup,
  Button,
  GenericCombobox,
  SelectGroup,
  DataBlock,
  Card,
  CardHeader,
  CardTitle,
  CheckboxGroup,
  NumericInputGroup
} from '@/common'
import Drawer from '@/shared/components/ui/Drawer'
import { Artifacts } from '@/api/EmissionReportModel'
import calculateCombustionEmissions from '@/features/reports/formulas/calcualteCombustionEmissions'
import { useAppConfig } from '@/shared/hooks/api/appConfig'
import useI18n from '@/shared/hooks/useI18n'
import { useEmissionFactorValidation } from '@/hooks/useEmissionFactorValidation'
import { usePermissions } from '@/shared/hooks/usePermissions'
import calculateFuelConsumptionInT from '@/features/reports/formulas/calculateFuelConsumptionInT'

function OxidationFactorCalculator({
  carbonInAsh,
  totalCarbon,
  setCarbonInAsh,
  setTotalCarbon,
  oxidationFactor,
  setIsCustomOxidationFactor,
  isCustomOxidationFactor
}: {
  carbonInAsh: string
  totalCarbon: string
  setCarbonInAsh: (value: string) => void
  setTotalCarbon: (value: string) => void
  oxidationFactor: number
  setIsCustomOxidationFactor: (value: boolean) => void
  isCustomOxidationFactor: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="space-y-1">
      <CheckboxGroup
        id="isCustomOxidationFactor"
        label={t(
          'report:calculators.combustionCaltulatorPanel.fields.isCustomOxidationFactor.label'
        )}
        checked={isCustomOxidationFactor}
        onChange={setIsCustomOxidationFactor}
      />
      <div
        className={cx('flex items-end gap-2 transition-all duration-300 overflow-hidden', {
          'h-[74px] opacity-100': isCustomOxidationFactor,
          'h-0 opacity-0': !isCustomOxidationFactor
        })}
      >
        <div className="shrink-0 max-w-32">
          <InputGroup
            disabled={!isCustomOxidationFactor}
            name="carbonInAsh"
            label={t('report:calculators.combustionCaltulatorPanel.fields.carbonInAsh.label')}
            value={carbonInAsh}
            onChange={e => setCarbonInAsh(e.target.value)}
          />
        </div>
        <div className="text-text-secondary p-2 text-2xl">/</div>
        <div className="shrink-0 max-w-32">
          <InputGroup
            disabled={!isCustomOxidationFactor}
            name="totalCarbon"
            label={t('report:calculators.combustionCaltulatorPanel.fields.totalCarbon.label')}
            value={totalCarbon}
            onChange={e => setTotalCarbon(e.target.value)}
          />
        </div>
        <div className="text-text-secondary p-2 text-2xl">=</div>
        <InputGroup
          disabled
          name="oxidationFactor"
          label={''}
          readOnly
          value={oxidationFactor.toFixed(2)}
        />
      </div>
    </div>
  )
}

export default function CombustionCalculatorPanel({
  isOpen,
  close,
  submit,
  initialValues
}: {
  isOpen: boolean
  close: () => void
  initialValues?: Artifacts
  submit: (emission: number, artifacts: Artifacts) => void
}) {
  const { combustionFuelTypesById } = useAppConfig().config
  const { t, numberFormatter3Decimals } = useI18n()
  const [isAdding, setIsAdding] = useState(false)
  const { checkPermission } = usePermissions()
  const canEditReport = checkPermission('emissionReport', 'update')
  const [state, setState] = useState({
    fuelConsumed: initialValues?.fuelConsumed || '',
    fuelType: initialValues?.fuelType || '',
    emissionFactor: initialValues?.emissionFactor || '',
    carbonInAsh: initialValues?.carbonInAsh || '',
    totalCarbon: initialValues?.totalCarbon || '',
    fuelConsumptionUnit: initialValues?.fuelConsumptionUnit || UnitLabel.t,
    fuelConsumptionInM3: initialValues?.fuelConsumptionInM3 || '',
    density: initialValues?.density || ''
  })

  const fuelTypeOptions = useMemo(() => {
    return Object.values(combustionFuelTypesById)
      .map(fuelType => ({
        value: fuelType.id,
        label: t(`common:fuelTypes.${fuelType.id}`)
      }))
      .sort((fuelTypeA, fuelTypeB) => fuelTypeA.label.localeCompare(fuelTypeB.label))
  }, [combustionFuelTypesById, t])

  const [isCustomOxidationFactor, setIsCustomOxidationFactor] = useState(false)

  const { useEmissionFactorIsOutOfRangeValidation } = useEmissionFactorValidation({
    fuelType: state.fuelType
  })

  const [emissionFactorWarning, setEmissionFactorWarning] = useState<string>('')

  const toggleOxidationFactor = (isChecked: boolean) => {
    setIsCustomOxidationFactor(isChecked)

    if (!isChecked) {
      setState(prev => ({ ...prev, carbonInAsh: '', totalCarbon: '' }))
    }
  }

  const updateFueltype = (value: string) => {
    const defaultEmissionFactor = combustionFuelTypesById[value]?.emissionFactor
      ? String(combustionFuelTypesById[value]?.emissionFactor)
      : ''

    setEmissionFactorWarning('')
    setState(prev => ({ ...prev, fuelType: value, emissionFactor: defaultEmissionFactor }))
  }

  const result = useMemo(() => {
    if (!combustionFuelTypesById[state.fuelType]) {
      return null
    }

    const { netCalorificValue } = combustionFuelTypesById[state.fuelType]

    return calculateCombustionEmissions({
      fuelConsumed: Number(state.fuelConsumed),
      netCalorificValuePgGg: netCalorificValue,
      emissionFactorTco2pTJ: Number(state.emissionFactor),
      carbonInAsh: state.carbonInAsh ? Number(state.carbonInAsh) : undefined,
      totalCarbon: state.totalCarbon ? Number(state.totalCarbon) : undefined
    })
  }, [state, combustionFuelTypesById])

  const isQuantityFieldFilled = useMemo(() => {
    return state.fuelConsumptionUnit === UnitLabel.t
      ? state.fuelConsumed !== ''
      : state.fuelConsumptionInM3 !== ''
  }, [state.fuelConsumptionUnit, state.fuelConsumed, state.fuelConsumptionInM3])

  const isValid = result && result.emission >= 0 && isQuantityFieldFilled
  const isAddDisabled = !isValid

  const resetState = useCallback(() => {
    setState({
      fuelConsumed: '',
      fuelType: '',
      emissionFactor: '',
      carbonInAsh: '',
      totalCarbon: '',
      fuelConsumptionUnit: UnitLabel.t,
      fuelConsumptionInM3: '',
      density: ''
    })
  }, [])

  const closeAndReset = useCallback(() => {
    resetState()
    close()
  }, [close, resetState])

  const onAddClick = async () => {
    setIsAdding(true)
    if (!isValid) {
      setIsAdding(false)
      return
    }

    const artifacts = state

    await submit(result.emission, artifacts)
    closeAndReset()
    setIsAdding(false)
  }

  const onEmissionFactorChange = (emissionFactor: string) => {
    setState(prev => ({ ...prev, emissionFactor }))

    const numberEmissionFactor = Number(emissionFactor)

    const validateData = useEmissionFactorIsOutOfRangeValidation.safeParse(numberEmissionFactor)
    if (!validateData.success && numberEmissionFactor > 0) {
      const warningMessage = validateData.error?.issues[0].message
      setEmissionFactorWarning(warningMessage)
    } else {
      setEmissionFactorWarning('')
    }
  }

  const onFuelConsumptionInM3Change = useCallback(
    (fuelConsumptionInM3: string) => {
      setState(prev => ({ ...prev, fuelConsumptionInM3: fuelConsumptionInM3 }))

      const fuelConsumptionInT = calculateFuelConsumptionInT(
        fuelConsumptionInM3 === '' ? 0 : Number(fuelConsumptionInM3),
        Number(state.density)
      )
      setState(prev => ({ ...prev, fuelConsumed: fuelConsumptionInT.toString() }))
    },
    [state]
  )

  const onDensityChange = useCallback(
    (density: string) => {
      setState(prev => ({ ...prev, density }))
      const fuelConsumptionInT = calculateFuelConsumptionInT(
        Number(state.fuelConsumptionInM3),
        density === '' ? 0 : Number(density)
      )
      setState(prev => ({ ...prev, fuelConsumed: fuelConsumptionInT.toString() }))
    },
    [state]
  )

  const onConsumptionUnitChange = useCallback((unit: string) => {
    setState(prev => ({ ...prev, fuelConsumptionUnit: unit as Unit }))
    setState(prev => ({ ...prev, fuelConsumed: '', fuelConsumptionInM3: '', density: '' }))
  }, [])

  const onFuelConsumedChange = useCallback((value: string) => {
    setState(prev => ({ ...prev, fuelConsumed: value }))
  }, [])

  useEffect(() => {
    if (initialValues) {
      setState(prev => ({ ...prev, ...initialValues }))
    }
  }, [isOpen, initialValues])

  const formattedEmission = `${numberFormatter3Decimals(result?.emission || 0)} ${UnitLabel.tCO2}`
  return (
    <Drawer isOpen={isOpen} sizeType="4xl" close={closeAndReset}>
      <Card className="min-w-[510px]">
        <CardHeader>
          <CardTitle title={t('report:calculators.combustionCaltulatorPanel.title')} />
        </CardHeader>
        <div className="space-y-4 p-4">
          <div className="space-y-4">
            <SelectGroup
              name="fuelConsumptionUnit"
              label={t(
                'report:calculators.combustionCaltulatorPanel.fields.fuelConsumptionUnit.label'
              )}
              value={state.fuelConsumptionUnit}
              onChange={({ target: { value } }) => {
                onConsumptionUnitChange(value)
              }}
              options={[
                { value: UnitLabel.t, label: UnitLabel.t },
                { value: UnitLabel.m3, label: UnitLabel.m3 }
              ]}
            />
            <NumericInputGroup
              type="text"
              name="fuelConsumed"
              label={t('report:calculators.combustionCaltulatorPanel.fields.fuelConsumed.label')}
              value={state.fuelConsumed}
              disabled={state.fuelConsumptionUnit !== UnitLabel.t}
              suffix={UnitLabel.t}
              error={
                state.fuelConsumptionUnit === UnitLabel.t && state.fuelConsumed === ''
                  ? t('common:validations.quantityRequiredZeroAllowed')
                  : undefined
              }
              numericFormat={{
                // Using the special `onValueChange` prop from the react-number-format library
                // to have access to the value without the thousand separators
                onValueChange: ({ value }) => {
                  onFuelConsumedChange(value)
                },
                decimalScale: 2,
                allowNegative: false,
                thousandSeparator: ','
              }}
            />
            {state.fuelConsumptionUnit === UnitLabel.m3 && (
              <div className="grid grid-cols-2 gap-2">
                <NumericInputGroup
                  type="text"
                  name="fuelConsumed"
                  label={t(
                    'report:calculators.combustionCaltulatorPanel.fields.fuelConsumed.label'
                  )}
                  value={state.fuelConsumptionInM3}
                  error={
                    state.fuelConsumptionUnit === UnitLabel.m3 && state.fuelConsumptionInM3 === ''
                      ? t('common:validations.quantityRequiredZeroAllowed')
                      : undefined
                  }
                  numericFormat={{
                    decimalScale: 2,
                    allowNegative: false,
                    thousandSeparator: ',',
                    onValueChange: ({ value }) => {
                      onFuelConsumptionInM3Change(value)
                    }
                  }}
                  suffix={UnitLabel.m3}
                />
                <NumericInputGroup
                  type="text"
                  name="density"
                  label={t('report:calculators.combustionCaltulatorPanel.fields.density.label')}
                  value={state.density}
                  suffix={UnitLabel.kgpM3}
                  onChange={e => onDensityChange(e.target.value)}
                />
              </div>
            )}
            <GenericCombobox<{ value: string; label: string }>
              label={t('report:calculators.combustionCaltulatorPanel.fields.fuelType.label')}
              items={fuelTypeOptions}
              value={fuelTypeOptions.find(option => option.value === state.fuelType) || null}
              onChange={option => updateFueltype(option ? option.value : '')}
              placeholder="-"
            />
            <div className="space-y-2">
              <h4 className="font-semibold text-sm text-text-secondary">
                {t('report:calculators.combustionCaltulatorPanel.defaults.label')}
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <DataBlock
                  data={`${combustionFuelTypesById[state.fuelType]?.emissionFactor || 0} ${UnitLabel.tCO2PerTJ}`}
                  label={t(
                    'report:calculators.combustionCaltulatorPanel.fields.emissionFactor.label'
                  )}
                />
                <DataBlock
                  data={`${combustionFuelTypesById[state.fuelType]?.netCalorificValue || 0} ${UnitLabel.GJpT}`}
                  label={t(
                    'report:calculators.combustionCaltulatorPanel.fields.netCalorificValue.label'
                  )}
                />
              </div>
            </div>
            <NumericInputGroup
              disabled={!state.fuelType}
              name="emissionFactor"
              label={t('report:calculators.combustionCaltulatorPanel.fields.emissionFactor.label')}
              value={state.emissionFactor}
              warning={emissionFactorWarning}
              suffix={UnitLabel.tCO2PerTJ}
              onChange={e => onEmissionFactorChange(e.target.value)}
              numericFormat={{
                decimalScale: 3
              }}
            />
            <OxidationFactorCalculator
              carbonInAsh={state.carbonInAsh}
              totalCarbon={state.totalCarbon}
              setCarbonInAsh={value => setState(prev => ({ ...prev, carbonInAsh: value }))}
              setTotalCarbon={value => setState(prev => ({ ...prev, totalCarbon: value }))}
              oxidationFactor={result?.parameters.OFi || 0}
              isCustomOxidationFactor={isCustomOxidationFactor}
              setIsCustomOxidationFactor={toggleOxidationFactor}
            />
          </div>

          <DataBlock data={formattedEmission} label={t('report:common:emission')} />
        </div>
        <footer className="grid grid-cols-2 gap-4 px-4 pb-4">
          <Button onClick={closeAndReset} variant="subtle">
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={isAddDisabled || isAdding || !canEditReport}
            isLoading={isAdding}
            onClick={onAddClick}
          >
            {t('common:actions.add')}
          </Button>
        </footer>
      </Card>
    </Drawer>
  )
}
